/**
 * Permission codes are `domain.resource.action`. Wildcards (`crm.*`, `*`) are allowed in role
 * definitions but never in checks. Scoped grants add `:scopeType:scopeId` at grant time.
 */
export const PERMISSIONS = [
  'admin.access',
  'admin.settings.write',
  'admin.audit.read',
  'admin.users.read',
  'admin.users.write',
  'admin.roles.write',
  'crm.people.read',
  'crm.people.write',
  'crm.organizations.read',
  'crm.organizations.write',
  'crm.tags.write',
  'crm.notes.write',
  'content.read',
  'content.write',
  'programs.write',
  'events.write',
  'applications.forms.write',
  'applications.read',
  'applications.review',
  'applications.decide',
  'documents.read.internal',
  'finance.read',
  'finance.refund',
  'finance.adjust',
  'finance.export',
  'fundraising.write',
  'accounting.manage',
  'maps.read.private',
  'maps.write',
  'scholarships.manage',
  'scholarships.review',
  'volunteers.manage',
  'volunteers.checkin',
  'sponsorships.manage',
  'commerce.manage',
  'communications.send',
  'communications.templates.write',
  'reports.read',
  'festival.alerts',
  'festival.checkin',
  'mutual_aid.manage',
] as const

export type Permission = (typeof PERMISSIONS)[number]

export interface RoleDefinition {
  code: string
  name: string
  description: string
  permissions: readonly (Permission | '*' | `${string}.*`)[]
}

export const SYSTEM_ROLES: readonly RoleDefinition[] = [
  {
    code: 'admin',
    name: 'Administrator',
    description: 'Full access to everything.',
    permissions: ['*'],
  },
  {
    code: 'staff',
    name: 'Staff',
    description: 'Day-to-day operations: CRM, content, applications, maps, volunteers, sponsors.',
    permissions: [
      'admin.access',
      'crm.*',
      'content.*',
      'programs.write',
      'events.write',
      'applications.*',
      'documents.read.internal',
      'maps.*',
      'volunteers.*',
      'sponsorships.manage',
      'communications.send',
      'reports.read',
      'finance.read',
      'festival.*',
    ],
  },
  {
    code: 'finance',
    name: 'Finance',
    description: 'Ledger, refunds, exports, accounting integration.',
    permissions: [
      'admin.access',
      'finance.*',
      'fundraising.write',
      'accounting.manage',
      'reports.read',
    ],
  },
  {
    code: 'reviewer',
    name: 'Reviewer',
    description: 'Scores assigned applications (scoped to a program or form).',
    permissions: ['admin.access', 'applications.review', 'scholarships.review'],
  },
  {
    code: 'map_editor',
    name: 'Map editor',
    description: 'Edits event maps and booth assignments.',
    permissions: ['admin.access', 'maps.*', 'applications.read', 'festival.checkin'],
  },
  {
    code: 'volunteer_lead',
    name: 'Volunteer lead',
    description: 'Manages volunteer shifts and check-in.',
    permissions: ['admin.access', 'volunteers.*', 'crm.people.read', 'festival.*'],
  },
  {
    code: 'member',
    name: 'Member',
    description: 'Default role for every signed-in user; portal access only.',
    permissions: [],
  },
] as const

export function permissionMatches(granted: string, required: Permission): boolean {
  if (granted === '*') return true
  if (granted.endsWith('.*')) return required.startsWith(granted.slice(0, -1))
  return granted === required
}

export function hasPermission(granted: Iterable<string>, required: Permission): boolean {
  for (const g of granted) if (permissionMatches(g, required)) return true
  return false
}
